import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getUsers,
  deleteUser,
  type UserRecord,
} from '../../services/userServices'
import { useNavigate } from 'react-router-dom';

export function UserManagement(){
  const navigate = useNavigate();
  const queryClient = useQueryClient()
  const [currentUserId] = useState<string | null>(() => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as { id?: string } | null
      return currentUser?.id ?? null
    } catch {
      return null
    }
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5

  // ---------------------------------------------------------------------------
  // Queries & Mutations
  // ---------------------------------------------------------------------------

  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['users', currentPage, searchQuery],
    queryFn: () =>
      getUsers({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
      }),
    placeholderData: (previousData) => previousData,
  })

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      window.alert('အသုံးပြုသူအကောင့် ဖျက်ပစ်ပြီးပါပြီ။')
    },
    onError: (err: Error) => {
      window.alert(`ဖျက်ပစ်ရာတွင် အမှားအယွင်းရှိပါသည်: ${err.message}`)
    },
  })

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDeleteClick = (user: UserRecord) => {
    if (user.id === currentUserId) return

    if (window.confirm(`အသုံးပြုသူ ${user.name ?? user.id} ကို ဖျက်ရန် သေချာပါသလား။`)) {
      deleteUserMutation.mutate(user.id)
    }
  }

  const users = usersData?.data ?? []
  const totalItems = usersData?.total ?? 0
  const totalPages = usersData?.totalPages ?? 1
  const startIndex = (currentPage - 1) * itemsPerPage

  // ---------------------------------------------------------------------------
  // Render User Table List
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4 font-sans antialiased">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">အသုံးပြုသူအကောင့်များ</h2>
        <p className="mt-1 text-sm text-slate-500">
          ပရိုဖိုင်များကို စီမံခန့်ခွဲခြင်း နှင့် စနစ်ခွင့်ပြုချက်များ သတ်မှတ်ခြင်း
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.603 10.603Z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="အမည်၊ အီးမေးလ် သို့မဟုတ် ဝန်ထမ်းကုဒ်ဖြင့် ရှာဖွေရန်"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setCurrentPage(1)
            }}
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန် "
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            <svg
              className={`h-4 w-4 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            နောက်ဆုံး Data များ ရယူရန် 
          </button>

          <button
            type="button"
             onClick={() => navigate('/user-form/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            အသစ်ဆောက်ရန်
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full table-fixed divide-y divide-slate-200 text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-28 px-4 py-3">ကုဒ်</th>
                <th className="px-6 py-3">အသုံးပြုသူ အချက်အလက်</th>
                <th className="w-36 px-4 py-3">အခန်းကဏ္ဍ</th>
                <th className="w-28 px-4 py-3">အခြေအနေ</th>
                <th className="w-24 px-6 py-3 text-right">လုပ်ဆောင်ချက်များ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white font-normal text-slate-900">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400 font-normal">
                    <div className="flex items-center justify-center gap-2">
                      <svg className="h-5 w-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>အချက်အလက်များ ရယူနေပါသည်...</span>
                    </div>
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-rose-500 font-normal">
                    အချက်အလက်များ ရယူရာတွင် အမှားအယွင်း ရှိနေပါသည်: {(error as Error)?.message}
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/75 transition-colors">
                    <td className="whitespace-nowrap px-4 py-3.5 text-xs font-mono font-semibold text-slate-600">
                      {user.employeeCode || '-'}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-800">{user.name}</span>
                        <span className="text-xs font-normal text-slate-500">{user.email}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          user.role === 'SUPER_ADMIN'
                            ? 'bg-purple-50 text-purple-700 ring-purple-600/20'
                            : user.role === 'ADMIN'
                            ? 'bg-blue-50 text-blue-700 ring-blue-700/10'
                            : 'bg-slate-50 text-slate-600 ring-slate-500/10'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                          user.status === 'ACTIVE'
                            ? 'bg-emerald-50 text-emerald-700'
                            : user.status === 'LOCKED'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-rose-50 text-rose-700'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            user.status === 'ACTIVE'
                              ? 'bg-emerald-500'
                              : user.status === 'LOCKED'
                              ? 'bg-amber-500'
                              : 'bg-rose-500'
                          }`}
                        />
                        {user.status || 'ACTIVE'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-3.5 text-right text-xs">
                      {user.id !== currentUserId && (
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => navigate(`/user-form/edit/${user.id}`)}
                            title="ပြင်ဆင်ရန်"
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-blue-600 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteClick(user)}
                            disabled={deleteUserMutation.isPending}
                            title="ဖျက်ရန်"
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-50 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.244 2.244 0 0 1-2.244 2.077H8.084a2.244 2.244 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400 font-normal">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 0 && !isLoading && !isError && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="text-sm text-slate-500">
              စုစုပေါင်း <span className="font-semibold text-slate-700">{totalItems}</span> ခုအနက်မှ{' '}
              <span className="font-semibold text-slate-700">{totalItems > 0 ? startIndex + 1 : 0}</span> မှ{' '}
              <span className="font-semibold text-slate-700">
                {Math.min(startIndex + itemsPerPage, totalItems)}
              </span>{' '}
              ပြသနေသည်။
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1 || isFetching}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>
              <div className="text-sm font-medium text-slate-700">
                စာမျက်နှာ {currentPage} / {totalPages}
              </div>
              <button
                type="button"
                disabled={currentPage >= totalPages || isFetching}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}